import { createContextApi, invalidatesOne, providesList, providesOne, emitPlatformEvent, createCursorPagedEndpoint, registerCrossContextInvalidation } from '@quickbee/store';
import { getContextClient } from '@quickbee/api-client';

// src/index.ts
var SCHEME_TAGS = ["Scheme", "Enrollment", "SipSchedule", "Deposit", "Refund"];
var membershipCardDownloadRequest = (enrollmentId) => ({
  url: `/schemes/enrollments/${enrollmentId}/card`,
  method: "GET",
  responseType: "binary"
});
function listParams(args) {
  const params = {};
  for (const [key, value] of Object.entries(args ?? {})) {
    if (typeof value === "string" && value.trim() === "") continue;
    params[key] = value;
  }
  return params;
}
var schemeEndpoints = (build) => ({
  // ─── /schemes ───────────────────────────────────────────────────────────────
  listSchemes: build.query({
    query: (params) => ({ url: "/schemes", params: listParams(params) }),
    ...createCursorPagedEndpoint(),
    providesTags: (result) => providesList("Scheme", result),
    // Reference data (§10.4): the catalogue changes rarely and is read constantly.
    keepUnusedDataFor: 900
  }),
  /**
   * The same catalogue read, paged **discretely** — one request, one page, no accumulation.
   *
   * {@link listSchemes} drops `cursor` from its cache key and appends every response into a
   * single entry, which is exactly right for a "load more" list and exactly wrong for a numbered
   * pager: paging forward would grow the rendered set instead of replacing it, and paging *back*
   * would re-fetch a walked cursor and append its rows a second time.
   *
   * So this one keeps the cursor in the cache key. Each page is its own entry, which also makes
   * *Previous* free. Both exist because both are legitimate — the same pair, for the same stated
   * reason, as api-audit's `listEntries` / `listEntriesPage`.
   *
   * `GET /schemes` computes `pagination.total` as a real `COUNT` over the same predicate as the
   * list, `status` included (B7), so a numbered pager here is exact and agrees by construction
   * with any badge reading the same figure.
   */
  listSchemesPage: build.query({
    query: (params) => ({ url: "/schemes", params: listParams(params) }),
    providesTags: (result) => providesList("Scheme", result),
    keepUnusedDataFor: 900
  }),
  /** Returns a plain list, not a page: the saver's catalogue is small by design. */
  listSchemesForCustomer: build.query({
    query: () => ({ url: "/schemes/for-customer" }),
    providesTags: (result) => providesList("Scheme", result),
    keepUnusedDataFor: 300
  }),
  /**
   * The closed catalogue of membership-card designs a scheme may be created
   * with. Reference data like the scheme catalogue itself (§10.4): it changes
   * only on a deploy, so it is held as long as the catalogue reads are.
   */
  listCardTemplates: build.query({
    query: () => ({ url: "/schemes/card-templates" }),
    keepUnusedDataFor: 900
  }),
  getScheme: build.query({
    query: (id) => ({ url: `/schemes/${id}` }),
    providesTags: (_r, _e, id) => providesOne("Scheme", id),
    keepUnusedDataFor: 900
  }),
  createScheme: build.mutation({
    query: ({ clientRequestId, ...body }) => ({
      url: "/schemes",
      method: "POST",
      body,
      idempotencyKey: `scheme-create:${clientRequestId}`
    }),
    invalidatesTags: [{ type: "Scheme", id: "LIST" }]
  }),
  /**
   * Send only the fields being changed. ADR-007 locks the financial-terms group
   * once a scheme has any enrollment, and the backend decides which group a
   * request touched by field presence — so echoing an unchanged `monthlyAmount`
   * back with a rename turns a permitted edit into a rejected one.
   */
  updateScheme: build.mutation({
    query: ({ id, ...body }) => ({
      url: `/schemes/${id}`,
      method: "PATCH",
      body,
      skipIdempotencyKey: true
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesOne("Scheme", id)
  }),
  activateScheme: build.mutation({
    query: (id) => ({ url: `/schemes/${id}/activate`, method: "POST", skipIdempotencyKey: true }),
    invalidatesTags: (_r, _e, id) => invalidatesOne("Scheme", id)
  }),
  /**
   * The approval queue's refusal (INV-40): `DRAFT -> REJECTED` with a mandatory
   * reason. Not terminal — editing the scheme returns it to `DRAFT`. Invalidates
   * the one scheme and the list, so the Awaiting and Rejected queues both move.
   */
  rejectScheme: build.mutation({
    query: ({ id, reason }) => ({
      url: `/schemes/${id}/reject`,
      method: "POST",
      body: { reason },
      skipIdempotencyKey: true
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesOne("Scheme", id)
  }),
  archiveScheme: build.mutation({
    query: (id) => ({ url: `/schemes/${id}/archive`, method: "POST", skipIdempotencyKey: true }),
    invalidatesTags: (_r, _e, id) => invalidatesOne("Scheme", id)
  }),
  // ─── /schemes/enrollments ───────────────────────────────────────────────────
  /**
   * A tenant/any-scoped caller (ops, ledger's account drawer) **must** pass
   * `customerId`; there is no unfiltered mode, and omitting it is a 400. An
   * `own`-scoped caller may omit it — see {@link ListEnrollmentsArgs}.
   */
  listEnrollments: build.query({
    query: (params) => ({ url: "/schemes/enrollments", params: listParams(params) }),
    ...createCursorPagedEndpoint(),
    providesTags: (result) => providesList("Enrollment", result),
    keepUnusedDataFor: 60
  }),
  /**
   * The same read, paged **discretely** — one request, one page, no accumulation.
   *
   * {@link listEnrollments} drops `cursor` from its cache key and appends every response into a
   * single entry, which is right for a "load more" list and wrong for a numbered pager: paging
   * forward would grow the rendered set rather than replace it, and paging *back* would re-fetch
   * a walked cursor and append its rows a second time. This one keeps the cursor in the cache
   * key, so each page is its own entry and Previous is answered from cache.
   *
   * Same pair, same reason, as `api-audit`'s `listEntries`/`listEntriesPage`.
   */
  listEnrollmentsPage: build.query({
    query: (params) => ({ url: "/schemes/enrollments", params: listParams(params) }),
    providesTags: (result) => providesList("Enrollment", result),
    keepUnusedDataFor: 60
  }),
  /** Schemes this customer may still open a plan in — a scheme projection, not an enrollment one. */
  listEligibleSchemes: build.query({
    query: (args) => ({
      url: "/schemes/enrollments/eligible",
      params: listParams(args ?? void 0)
    }),
    providesTags: (result) => providesList("Scheme", result),
    keepUnusedDataFor: 300
  }),
  getEnrollment: build.query({
    query: (id) => ({ url: `/schemes/enrollments/${id}` }),
    providesTags: (_r, _e, id) => providesOne("Enrollment", id),
    keepUnusedDataFor: 60
  }),
  /** Empty for a FLEXIBLE plan — the schedule is the SCHEDULED_SIP half of ADR-011. */
  getSipSchedule: build.query({
    query: (enrollmentId) => ({ url: `/schemes/enrollments/${enrollmentId}/sip-schedule` }),
    providesTags: (_r, _e, id) => [{ type: "SipSchedule", id }],
    keepUnusedDataFor: 60
  }),
  /**
   * Plain list: a FLEXIBLE plan's deposits are bounded by its eleven-month window.
   * Returns `[]` rather than an error for a SCHEDULED_SIP enrollment, so a drawer
   * showing both plan kinds need not branch on mode just to avoid a 4xx.
   */
  getEnrollmentDeposits: build.query({
    query: (enrollmentId) => ({ url: `/schemes/enrollments/${enrollmentId}/deposits` }),
    providesTags: (_r, _e, enrollmentId) => [
      { type: "Deposit", id: enrollmentId }
    ],
    keepUnusedDataFor: 60
  }),
  createEnrollment: build.mutation({
    query: ({ draftId, ...body }) => ({
      url: "/schemes/enrollments",
      method: "POST",
      body,
      // I9, verbatim from the design: caller-stable, derived from the mutation's
      // identity. A customer who taps "Enroll" twice gets one enrollment.
      idempotencyKey: `enroll:${body.schemeId}:${draftId}`
    }),
    invalidatesTags: [
      { type: "Enrollment", id: "LIST" },
      // The scheme's `enrolled` count and, on the for-customer projection, its
      // `canOpenAnother` both just changed.
      { type: "Scheme", id: "LIST" }
    ],
    async onQueryStarted(_arg, { queryFulfilled }) {
      try {
        const { data } = await queryFulfilled;
        emitPlatformEvent("enrollment.created", {
          enrollmentId: data.id,
          schemeId: data.schemeId,
          customerId: data.customerId
        });
      } catch {
      }
    }
  }),
  cancelEnrollment: build.mutation({
    query: ({ id, reason }) => ({
      url: `/schemes/enrollments/${id}/cancel`,
      method: "POST",
      body: { reason },
      idempotencyKey: `enrollment-cancel:${id}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("Enrollment", id),
      { type: "SipSchedule", id },
      // A cancelled plan stops blocking re-enrollment, so the for-customer row
      // flips back to AVAILABLE.
      { type: "Scheme", id: "LIST" },
      // Cancelling a currency plan is what creates its refund (§4.3a step 2), so
      // the screen that asked for it before the cancellation must ask again.
      ...invalidatesOne("Refund", id)
    ]
  }),
  // ─── The D5 cancellation refund (currency-ledger-design §4.3a) ─────────────
  /**
   * The refund a currency cancellation opened, or `null`.
   *
   * Null rather than a 404 for a plan with no refund: a gold plan and an
   * un-cancelled plan both legitimately have none, and the saver's screen asks
   * this before it knows the answer.
   *
   * Invalidated by `cancelEnrollment` — the cancellation is what creates it.
   */
  getEnrollmentRefund: build.query({
    query: (id) => ({ url: `/schemes/enrollments/${id}/refund` }),
    providesTags: (_r, _e, id) => providesOne("Refund", id)
  }),
  /**
   * The operations refunds queue: every refund holding a line nobody is moving —
   * a manual payout awaiting an operator, or a failed gateway line awaiting a
   * decision. `schemes:refund:tenant`.
   *
   * Unpaged, like payment's offline-collection queue: a list of money the
   * platform owes savers and nobody is moving should be short enough to read in
   * one screen, and a pager would hide it when it is not.
   */
  listOpenRefunds: build.query({
    query: () => ({ url: "/schemes/enrollments/refunds/open" }),
    providesTags: (result) => providesList("Refund", result, "id")
  }),
  /**
   * An operator records that a manual payout line has actually been paid.
   * `schemes:refund:tenant` — deliberately not the grant that cancelled the plan.
   *
   * The idempotency key names the line, not the refund: two lines of one refund
   * are two payouts, and a key naming only the refund would swallow the second.
   */
  completeRefundLine: build.mutation({
    query: ({ id, refundId, lineId, payoutReference }) => ({
      url: `/schemes/enrollments/${id}/refunds/${refundId}/lines/${lineId}/complete`,
      method: "POST",
      body: { payoutReference },
      idempotencyKey: `refund-line-complete:${lineId}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("Refund", id),
      // The queue is a list of open lines, and completing one is exactly what takes it off.
      { type: "Refund", id: "LIST" }
    ]
  }),
  /** Sends a FAILED gateway line down the manual-payout road. `schemes:refund:tenant`. */
  requeueRefundLine: build.mutation({
    query: ({ id, refundId, lineId }) => ({
      url: `/schemes/enrollments/${id}/refunds/${refundId}/lines/${lineId}/requeue`,
      method: "POST",
      idempotencyKey: `refund-line-requeue:${lineId}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("Refund", id),
      { type: "Refund", id: "LIST" }
    ]
  }),
  /**
   * Reverses a posted refund's ledger legs. Refused server-side once any line has
   * paid out — a paid line cannot be un-paid by a state change.
   *
   * Keyed on a caller-supplied `requestId` rather than the refund id, for the
   * reason `api-tenant`'s reversible transitions are: a reverse → re-request →
   * reverse cycle inside the 24-hour window would otherwise replay the first.
   */
  reverseEnrollmentRefund: build.mutation({
    query: ({ id, refundId, reason, requestId }) => ({
      url: `/schemes/enrollments/${id}/refunds/${refundId}/reverse`,
      method: "POST",
      body: { reason },
      idempotencyKey: `refund-reverse:${refundId}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("Refund", id),
      ...invalidatesOne("Enrollment", id)
    ]
  }),
  /** Ops/RM action once the customer has caught up out-of-band (INV-20); `schemes:enroll:any` only. */
  resumeEnrollment: build.mutation({
    query: ({ id }) => ({
      url: `/schemes/enrollments/${id}/resume`,
      method: "POST",
      skipIdempotencyKey: true
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesOne("Enrollment", id)
  }),
  /**
   * ADR-010 — ops write-off of a permanently-MISSED instalment; `schemes:enroll:any`
   * only, and `reason` is mandatory (it is the sole audit note). Returns 204, so
   * the screen refetches the enrollment and its schedule.
   */
  waiveInstallment: build.mutation({
    query: ({ enrollmentId, installmentId, reason }) => ({
      url: `/schemes/enrollments/${enrollmentId}/installments/${installmentId}/waive`,
      method: "POST",
      body: { reason },
      // A waiver moves money. Keyed on the instalment, so a double-click waives once.
      idempotencyKey: `installment-waive:${installmentId}`
    }),
    invalidatesTags: (_r, _e, { enrollmentId }) => [
      ...invalidatesOne("Enrollment", enrollmentId),
      { type: "SipSchedule", id: enrollmentId }
    ]
  })
});
function schemeClient() {
  return getContextClient({ context: "scheme" });
}

// src/membershipCard.ts
function downloadMembershipCard(enrollmentId) {
  return schemeClient().request(membershipCardDownloadRequest(enrollmentId));
}

// src/index.ts
var schemeApi = createContextApi({
  context: "scheme",
  reducerPath: "schemeApi",
  tagTypes: SCHEME_TAGS,
  endpoints: schemeEndpoints
});
var SCHEME_INVALIDATION_RULES = [
  {
    event: "payment.captured",
    invalidate: ({ enrollmentId }) => enrollmentId ? [
      { type: "Enrollment", id: enrollmentId },
      { type: "Enrollment", id: "LIST" },
      { type: "SipSchedule", id: enrollmentId },
      { type: "Deposit", id: enrollmentId },
      { type: "Scheme", id: "LIST" }
    ] : []
  },
  {
    event: "redemption.settled",
    // A settled redemption changes accrued grams on whichever enrollment it drew
    // from; the id is not on the event, so refresh the lists rather than guess.
    invalidate: () => [
      { type: "Enrollment", id: "LIST" },
      { type: "Scheme", id: "LIST" }
    ]
  }
];
function registerSchemeInvalidation(store) {
  return registerCrossContextInvalidation({
    store,
    api: schemeApi,
    rules: SCHEME_INVALIDATION_RULES
  });
}
var {
  useListSchemesQuery,
  useListSchemesPageQuery,
  useListSchemesForCustomerQuery,
  useListCardTemplatesQuery,
  useGetSchemeQuery,
  useCreateSchemeMutation,
  useUpdateSchemeMutation,
  useActivateSchemeMutation,
  useRejectSchemeMutation,
  useArchiveSchemeMutation,
  useListEnrollmentsQuery,
  useListEnrollmentsPageQuery,
  useListEligibleSchemesQuery,
  useGetEnrollmentQuery,
  useGetSipScheduleQuery,
  useGetEnrollmentDepositsQuery,
  useCreateEnrollmentMutation,
  useCancelEnrollmentMutation,
  useResumeEnrollmentMutation,
  useWaiveInstallmentMutation,
  // The D5 cancellation refund (§4.3a). `getEnrollmentRefund` is the saver's read;
  // the other four are the operations refunds queue and need `schemes:refund:tenant`.
  useGetEnrollmentRefundQuery,
  useListOpenRefundsQuery,
  useCompleteRefundLineMutation,
  useRequeueRefundLineMutation,
  useReverseEnrollmentRefundMutation
} = schemeApi;

export { SCHEME_INVALIDATION_RULES, SCHEME_TAGS, downloadMembershipCard, registerSchemeInvalidation, schemeApi, useActivateSchemeMutation, useArchiveSchemeMutation, useCancelEnrollmentMutation, useCompleteRefundLineMutation, useCreateEnrollmentMutation, useCreateSchemeMutation, useGetEnrollmentDepositsQuery, useGetEnrollmentQuery, useGetEnrollmentRefundQuery, useGetSchemeQuery, useGetSipScheduleQuery, useListCardTemplatesQuery, useListEligibleSchemesQuery, useListEnrollmentsPageQuery, useListEnrollmentsQuery, useListOpenRefundsQuery, useListSchemesForCustomerQuery, useListSchemesPageQuery, useListSchemesQuery, useRejectSchemeMutation, useRequeueRefundLineMutation, useResumeEnrollmentMutation, useReverseEnrollmentRefundMutation, useUpdateSchemeMutation, useWaiveInstallmentMutation };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map